import React, { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import { Grid, Section, Stack, Help, View } from '@gogame/components';
import { FormField, FormFieldGroup, withForm, SidePageV2 } from '@gogame/form';
import { usePages } from '@gogame/screens';

import { useCreateDepartment } from 'scenes/Users/AdminUsers/Departments';
import { useCreatePosition } from 'scenes/Users/AdminUsers/Positions';
import {
  loadManagers,
  loadDepartments,
  loadPositions,
  loadCompanies,
  loadStudios,
  loadSuperTeams,
  loadTeams,
  loadActions,
  loadCustomerCompanies,
  loadApplications,
  loadNetworks,
  loadAffiliates,
} from 'scenes/Users/AdminUsers/sources/fields';
import {
  UserPermissions,
  PermissionPickerCategory,
  PermissionPicker,
} from 'scenes/Users/components/UserPermissions';
import {
  CATEGORY_ACTIONS,
  CATEGORY_AFFILIATES,
  CATEGORY_APPLICATIONS,
  CATEGORY_CUSTOMER_COMPANIES,
  CATEGORY_NETWORKS,
  CATEGORY_PAGES,
} from 'scenes/Users/components/UserPermissions/constants';
import { PERMISSION_ACTION_SEE_ALL_AFFILIATES } from 'scenes/Users/AdminUsers/constants';
import { mapPageToOptions } from 'scenes/Users/utils';
import { usePermissionChange } from 'scenes/Users/AdminUsers/hooks';

import RolePicker from './components/RolePicker';
import {
  NAME,
  TEAM,
  SUPER_TEAM,
  POSITION,
  MANAGER,
  DEPARTMENT,
  EMAIL,
  PROFILE,
  PERMISSIONS,
  COMPANY,
  STUDIO,
  IS_SYSTEM,
  IS_TEST,
  IS_GROUP,
  IS_NEVER_LOCK,
} from './constants';
import { userSchema } from './schemas';

const CheckboxLabelWithHint = ({ children, hint }) => {
  return (
    <Stack
      as={'span'}
      direction={'row'}
      spacing={1}
      sx={{
        whiteSpace: 'nowrap',
        mr: '-16px',
      }}
    >
      <span> {children}</span>
      <Help>{hint}</Help>
    </Stack>
  );
};

const UserForm = withForm({
  validationSchema: userSchema,
})(({
  onClose,
  onExited,
  open,
  title,
  subTitle,
  editMode = false,
  loading,
  entityId,
  values,
  setFieldValue,
}) => {
  const pages = usePages(mapPageToOptions, {
    allowDuplicatePermission: false,
    allowHidden: false,
  });

  const sortedPages = useMemo(() => {
    return orderBy(pages, ['name'], ['asc']);
  }, [pages]);

  const { isLoading, onCustomerCompaniesChange } = usePermissionChange({
    values,
    setFieldValue,
  });

  const [createPosition] = useCreatePosition();

  const [createDepartment] = useCreateDepartment();

  const isOptionManagerDisabled = ({ id }) => values.id && id === values.id;

  const [rolePickerLoading, setRolePickerLoading] = React.useState(false);

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section
          title={'User information'}
          isCollapsable={true}
          initialOpen={true}
        >
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Full name'}
                required={true}
                padding={0}
                fixAutoComplete={true}
                name={NAME}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Email'}
                fixAutoComplete={true}
                name={EMAIL}
                required={true}
                disabled={editMode}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Company'}
                name={COMPANY}
                padding={0}
                source={loadCompanies}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Studio'}
                name={STUDIO}
                padding={0}
                source={loadStudios}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Department'}
                name={DEPARTMENT}
                padding={0}
                source={loadDepartments}
                required={true}
                addOptionLabel={'Department'}
                onAdd={createDepartment}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Team'}
                name={TEAM}
                padding={0}
                source={loadTeams}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Super team'}
                name={SUPER_TEAM}
                padding={0}
                source={loadSuperTeams}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Manager'}
                name={MANAGER}
                source={loadManagers}
                isOptionDisabled={isOptionManagerDisabled}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Position'}
                name={POSITION}
                padding={0}
                source={loadPositions}
                required={true}
                addOptionLabel={'Position'}
                onAdd={createPosition}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Profile'}
                name={PROFILE}
                helper={'Corporate or personal profile URL'}
                padding={0}
              />
            </Grid>
            <Grid
              item
              container
              xs={8}
              alignItems={'baseline'}
              direction={'row'}
            >
              <Stack spacing={2.5} direction={'row'}>
                <FormField.Checkbox
                  name={IS_SYSTEM}
                  label={
                    <CheckboxLabelWithHint
                      hint={
                        'Not a real user - a bot that needs the credentials to login'
                      }
                    >
                      Bot with credentials
                    </CheckboxLabelWithHint>
                  }
                  padding={0}
                />
                <FormField.Checkbox
                  name={IS_GROUP}
                  label={
                    <CheckboxLabelWithHint
                      hint={
                        'Used for Big Query - individual email or an email group'
                      }
                    >
                      User group
                    </CheckboxLabelWithHint>
                  }
                  padding={0}
                />

                <FormField.Checkbox
                  name={IS_NEVER_LOCK}
                  label={
                    <CheckboxLabelWithHint
                      hint={
                        'User will not be auto-blocked by the system due to inactivity'
                      }
                    >
                      Never block user
                    </CheckboxLabelWithHint>
                  }
                  padding={0}
                />
                <FormField.Checkbox name={IS_TEST} label={'Test'} padding={0} />
              </Stack>
            </Grid>
          </Grid>
        </Section>
        <Section
          title={'Permissions'}
          fullHeight={true}
          isCollapsable={true}
          initialOpen={true}
        >
          <View loading={isLoading || rolePickerLoading}>
            <FormFieldGroup name={PERMISSIONS}>
              <UserPermissions
                roleSelect={<RolePicker setLoading={setRolePickerLoading} />}
              >
                <PermissionPickerCategory
                  id={CATEGORY_ACTIONS}
                  name={CATEGORY_ACTIONS}
                  label={'Actions'}
                  source={loadActions}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_PAGES}
                  name={CATEGORY_PAGES}
                  label={'Pages'}
                  options={sortedPages}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_CUSTOMER_COMPANIES}
                  name={CATEGORY_CUSTOMER_COMPANIES}
                  label={'Customer Companies'}
                  source={loadCustomerCompanies}
                >
                  <PermissionPicker onChange={onCustomerCompaniesChange} />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_AFFILIATES}
                  name={CATEGORY_AFFILIATES}
                  label={'Affiliates'}
                  depends={[`${PERMISSIONS}.${CATEGORY_CUSTOMER_COMPANIES}`]}
                  source={loadAffiliates}
                  disabled={values[PERMISSIONS]?.[CATEGORY_ACTIONS]?.includes(
                    PERMISSION_ACTION_SEE_ALL_AFFILIATES
                  )}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_NETWORKS}
                  name={CATEGORY_NETWORKS}
                  label={'Networks'}
                  source={loadNetworks}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_APPLICATIONS}
                  name={CATEGORY_APPLICATIONS}
                  label={'Applications'}
                  source={loadApplications}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
              </UserPermissions>
            </FormFieldGroup>
          </View>
        </Section>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default UserForm;
